import { HtmlValues, HtmlGathererSettings, JQueryInput, Skyward } from "./types";

const skyward = window.skyward;

const defaultSettings: HtmlGathererSettings =
{
    stripJavaScript: false,
    isMassPrintOnlineForm: false,
};

export function getHtml(html: JQueryInput, options?: HtmlGathererSettings): Promise<string> 
{   
    return getNode(html, options).then(node => node.html());
};

export async function getNode(html: JQueryInput, options?: HtmlGathererSettings): Promise<JQuery> 
{
    const settings = $.extend({}, defaultSettings, options);
    const originalHtml: JQuery<HTMLElement> = $(html);

    if (settings.isMassPrintOnlineForm) 
    {
        // Mass Print Online Form does weird things, don't worry about it
        return originalHtml;
    }
    const clonedHtml = originalHtml.clone();

    if (settings.stripJavaScript) 
    {
        clonedHtml.find("script").remove(); // Strip all pre-existing JavaScript from the clone
    }
    setValuesForInputs(getValuesForInputs(originalHtml), clonedHtml);
    setValuesForSelects(getValuesForSelects(originalHtml), clonedHtml);
    setCanvasImages(originalHtml, clonedHtml);
    await setIframeSrcdocs(originalHtml, clonedHtml, settings);

    return clonedHtml;
};

export function getOuterHtml(html: JQuery<Node> | Node): string | undefined
{   
    let node: Node = isJQuery(html) ? html[0] : html;
    if (!node) 
    {
        return;
    }
    const elem = node.nodeType === Node.DOCUMENT_NODE ? (node as Document).documentElement : node as Element;
    const doctype = elem.ownerDocument ? elem.ownerDocument.doctype : undefined;
    const doctypeStr = doctype ? new XMLSerializer().serializeToString(doctype) : "";
    return doctypeStr + elem.outerHTML;
};

export async function getValues(html: JQuery): Promise<HtmlValues> 
{   
    const result: HtmlValues =
    {
        inputs: getValuesForInputs(html),
        selects: getValuesForSelects(html),
        iframes: await getValuesForIframes(html),
    };
    return result;
};

export async function setValues(values: HtmlValues, html: JQuery): Promise<void> 
{
    setValuesForInputs(values.inputs, html);
    setValuesForSelects(values.selects, html);
    await setValuesForIframes(values.iframes, html);
};

function getValuesForInputs(originalHtml: JQuery): (string | number | string[])[] 
{
    return originalHtml.find(":is(input,textarea):not(.doNotPost:hidden)").toArray().map(function (elem) 
    {
        const input = $(elem);
        return input.is(":checkbox") || input.is(":radio")
            ? input.is(":checked")
            : input.val();
    }) as (string | number | string[])[];
};

function setValuesForInputs(values: (string | number | boolean | string[] | undefined)[], html: JQuery): void 
{
    html.find(":is(input,textarea):not(.doNotPost:hidden)").each(function (index) 
    {
        const originalValue = values[index];
        const input = $(this);

        if (input.is(":checkbox") || input.is(":radio")) 
        {
            input.attr("checked", originalValue ? "checked" : null);
        }
        else if (input.is("input"))
        {
            input.attr("value", originalValue as string);
        }
        else
        {
            input.text(originalValue as string);
        }
    });
};

function getValuesForSelects(html: JQuery): number[] 
{
    return html.find('select:not(.doNotPost:hidden)').toArray().map(function (elem) 
    {
        return (elem as HTMLSelectElement).selectedIndex;
    });
};

function setValuesForSelects(values: (number | undefined)[], clonedHtml: JQuery<HTMLElement>): void 
{
    clonedHtml.find("select:not(.doNotPost:hidden)").each(function (index) 
    {
        const selectedIndex = values[index];
        $(this).find("option").each(function (optionIndex) 
        {
            const option = $(this);
            if (selectedIndex === optionIndex) 
            {
                option.attr("selected", "selected");

                // EvoPDF disregards label attributes and only renders options' inner text
                const label = option.attr("label");
                if (label !== undefined) 
                {
                    option.text(label);
                }
            }
            else
            {
                option.removeAttr("selected");
            }
        });
    });
};

function getValuesForIframes(originalHtml: JQuery): Promise<HtmlValues[]> 
{ 
    return Promise.all(originalHtml.find("iframe").toArray().map(async function (elem) 
    {
        // chrome adds an iframe when you translate a page; ignore errors from it
        try 
        {
            return await skyward.iframeListener.getHtmlValues(elem);
        }
        catch (ex) 
        {
            return {
                inputs: [],
                selects: [],
                iframes: []
            };
        }
    }));
};

function setValuesForIframes(values: any[], clonedHtml: JQuery): Promise<void[]> 
{
    return Promise.all(clonedHtml.find("iframe").toArray().map(function (iframe, iframeIndex) 
    {
        return skyward.iframeListener.setHtmlValues(iframe, values[iframeIndex]);
    }));
};

const setIframeSrcdocs = async function (originalHtml: JQuery, clonedHtml: JQuery, options: HtmlGathererSettings)
{
    const originalIframes = originalHtml.find("iframe");
    return await Promise.all(clonedHtml.find("iframe").toArray().map(async function (iframe, index) 
    {
        let outerHtmlValue: string | undefined;
        try 
        {
            outerHtmlValue = await skyward.iframeListener.getOuterHtml(originalIframes[index], options);
        } catch (ex) { }
        if (outerHtmlValue) 
        {
            $(iframe).attr("srcdoc", outerHtmlValue);
        }
    }));
};

const setCanvasImages = function (originalHtml: JQuery, clonedHtml: JQuery): void 
{
    const originalCanvases = originalHtml.find("canvas");
    clonedHtml.find("canvas").toArray().forEach((canvas, index) =>
    {
        const original = originalCanvases[index] as HTMLCanvasElement;
        try
        {
            const encoded = original.toDataURL();
            // At non-100% zoom, the CSS size may be different from the dimensions specified by the canvas's attributes
            const style = original.getBoundingClientRect();
            const image = $("<img>").attr({
                width: style.width || original.width,
                height: style.height || original.height,
                src: encoded
            });
            $(canvas).replaceWith(image);
        } catch (ex) { }
    });
};

function isJQuery<T>(elem: any): elem is JQuery<T>
{
    return elem instanceof $;
};